import { useEffect, useState, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Download, Radio } from 'lucide-react';
import api from '../utils/api';
import DateRangePicker from '../components/DateRangePicker';
import { exportCsv } from '../utils/exportCsv';

function KPICard({ label, value, sub, color, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl p-5 shadow-sm border border-gray-100 transition-all
        ${onClick ? 'cursor-pointer hover:shadow-md hover:border-[#E8761A] hover:scale-[1.02]' : ''}`}
    >
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</div>
      <div className={`text-3xl font-bold ${color || 'text-gray-800'}`}>{value ?? '—'}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
      {onClick && <div className="text-xs text-[#E8761A] mt-2 font-medium">Click to view →</div>}
    </div>
  );
}

function JobsDrawer({ filter, dateFrom, dateTo, branches, onClose }) {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [total, setTotal]     = useState(0);
  const [branch, setBranch]   = useState('');
  const [exporting, setExporting] = useState(false);
  const PER_PAGE = 50;

  const FILTER_LABELS = {
    all: 'All Jobs', pending: 'Pending Jobs', in_progress: 'In Progress Jobs',
    completed: 'Completed Jobs', passed: 'Passed Jobs', flagged: 'Flagged Jobs',
  };

  // Build the param sets for the current filter (one entry per underlying API call)
  const buildRequests = useCallback((p, perPage) => {
    const dateParams = {};
    if (dateFrom) dateParams.date_from = dateFrom;
    if (dateTo)   dateParams.date_to   = dateTo;
    const branchParams = branch ? { branch } : {};

    if (filter === 'all') {
      return [
        { url: '/jobs/',          params: { per_page: perPage, page: p, ...dateParams, ...branchParams } },
        { url: '/jobs/completed', params: { per_page: perPage, page: p, ...dateParams, ...branchParams } },
      ];
    } else if (filter === 'pending') {
      return [{ url: '/jobs/', params: { status: 'pending', per_page: perPage, page: p, ...dateParams, ...branchParams } }];
    } else if (filter === 'in_progress') {
      return [
        { url: '/jobs/', params: { status: 'audited', per_page: perPage, page: p, ...branchParams } },
        { url: '/jobs/', params: { status: 'flagged', per_page: perPage, page: p, ...branchParams } },
      ];
    } else if (filter === 'completed') {
      return [{ url: '/jobs/completed', params: { per_page: perPage, page: p, ...dateParams, ...branchParams } }];
    } else if (filter === 'passed') {
      return [{ url: '/jobs/completed', params: { result: 'Pass', per_page: perPage, page: p, ...dateParams, ...branchParams } }];
    } else if (filter === 'flagged') {
      return [{ url: '/jobs/completed', params: { result: 'Flag', per_page: perPage, page: p, ...dateParams, ...branchParams } }];
    }
    return [];
  }, [filter, dateFrom, dateTo, branch]);

  const fetchJobs = useCallback(async (p) => {
    setLoading(true);
    try {
      const reqs = buildRequests(p, PER_PAGE);
      const results = await Promise.all(reqs.map(r => api.get(r.url, { params: r.params })));
      setJobs(results.flatMap(r => r.data.jobs || []));
      setTotal(results.reduce((sum, r) => sum + (r.data.total || 0), 0));
    } finally { setLoading(false); }
  }, [buildRequests]);

  useEffect(() => { setPage(1); }, [filter, branch, dateFrom, dateTo]);
  useEffect(() => { fetchJobs(page); }, [fetchJobs, page]);

  async function handleExport() {
    setExporting(true);
    try {
      const reqs = buildRequests(1, 200);
      let all = [];
      for (const r of reqs) {
        let p = 1, totalPages = 1;
        do {
          const res = await api.get(r.url, { params: { ...r.params, page: p } });
          all = all.concat(res.data.jobs || []);
          totalPages = res.data.pages || 1;
          p++;
        } while (p <= totalPages);
      }
      exportCsv(`${filter}-jobs${branch ? `-${branch}` : ''}.csv`, all.map(j => ({
        Job: j.job_number, Branch: j.branch, Tech: j.primary_tech,
        Type: j.job_type, Scheduled: j.scheduled_date?.slice(0,10),
        Result: j.result || j.audit_status, Score: j.score != null ? Math.round(j.score*100)+'%' : '',
        Notes: j.notes || '',
      })));
    } finally { setExporting(false); }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-4xl bg-white z-50 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-[#3D2B1A]">
          <div>
            <h2 className="text-lg font-bold text-white">{FILTER_LABELS[filter]}</h2>
            {!loading && <p className="text-xs text-orange-200 mt-0.5">{total.toLocaleString()} job{total !== 1 ? 's' : ''}</p>}
          </div>
          <div className="flex items-center gap-3">
            {branches?.length > 0 && (
              <select value={branch} onChange={e => setBranch(e.target.value)}
                className="text-xs bg-white/10 text-white border border-white/20 rounded-lg px-2 py-1.5 outline-none">
                <option value="" className="text-gray-700">All Branches</option>
                {branches.map(b => <option key={b} value={b} className="text-gray-700">{b}</option>)}
              </select>
            )}
            <button onClick={handleExport} disabled={exporting}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white/10 hover:bg-white/20 text-white rounded-lg disabled:opacity-50">
              <Download size={13} /> {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
            <button onClick={onClose} className="text-white hover:text-orange-300 text-2xl font-bold leading-none">&times;</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? <div className="p-8 text-gray-400 text-center">Loading...</div>
          : jobs.length === 0 ? <div className="p-8 text-gray-400 text-center">No jobs found.</div>
          : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Job #','Branch','Technician','Job Type','Scheduled','Result','Score','Notes'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobs.map((job, i) => (
                  <tr key={job.id} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/40'}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-[#3D2B1A]">{job.job_number}</td>
                    <td className="px-4 py-3 text-gray-700">{job.branch}</td>
                    <td className="px-4 py-3 text-gray-700">{job.primary_tech}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{job.job_type}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{job.scheduled_date?.slice(0,10)}</td>
                    <td className="px-4 py-3">
                      {job.result === 'Pass' && <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">Pass</span>}
                      {job.result === 'Flag' && <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">Flag</span>}
                      {!job.result && <span className="text-gray-400 text-xs">{job.audit_status}</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{job.score != null ? `${Math.round(job.score*100)}%` : '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{job.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {total > PER_PAGE && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <span className="text-xs text-gray-500">Page {page} of {Math.ceil(total/PER_PAGE)}</span>
            <div className="flex gap-2">
              <button disabled={page===1} onClick={() => setPage(p=>p-1)} className="px-3 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100">← Prev</button>
              <button disabled={page>=Math.ceil(total/PER_PAGE)} onClick={() => setPage(p=>p+1)} className="px-3 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100">Next →</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ActiveAudits() {
  const [active, setActive]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => api.get('/jobs/active').then(r => setActive(r.data)).catch(() => {}).finally(() => setLoading(false));
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, []);

  if (loading || active.length === 0) return null;

  function elapsed(lockedAt) {
    const mins = Math.floor((Date.now() - new Date(lockedAt + 'Z').getTime()) / 60000);
    return mins < 1 ? 'just now' : `${mins}m ago`;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-amber-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-amber-100 bg-amber-50 flex items-center gap-2">
        <Radio size={15} className="text-amber-600 animate-pulse" />
        <h2 className="text-base font-semibold text-amber-800">Actively Being Audited</h2>
        <span className="ml-auto text-xs text-amber-500">{active.length} job{active.length !== 1 ? 's' : ''} in progress</span>
      </div>
      <div className="divide-y divide-gray-50">
        {active.map(job => (
          <div key={job.id} className="px-6 py-3 flex items-center gap-4 hover:bg-amber-50/30">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-mono text-sm font-semibold text-[#E8761A]">#{job.job_number}</span>
              <span className="text-gray-400 text-xs mx-2">·</span>
              <span className="text-sm text-gray-700">{job.primary_tech}</span>
              <span className="text-gray-400 text-xs mx-2">·</span>
              <span className="text-xs text-gray-500">{job.branch}</span>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-medium text-gray-700">{job.auditor_name}</div>
              <div className="text-xs text-gray-400">Started {elapsed(job.locked_at)}</div>
            </div>
            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200 flex-shrink-0">
              In Progress
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [drawer,    setDrawer]    = useState(null);
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');

  const loadData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;
    api.get('/reports/dashboard', { params })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  function handleDateChange(from, to) { setDateFrom(from); setDateTo(to); }

  function exportBranchScorecard() {
    if (!data) return;
    exportCsv('branch-scorecard.csv', data.branches.map(b => ({
      Branch: b.branch, 'Total Jobs': b.total, Pending: b.pending ?? 0,
      Completed: b.audited ?? 0, Passed: b.passed ?? 0, Flagged: b.flagged ?? 0,
      'Pass Rate': b.pass_rate != null ? b.pass_rate + '%' : '',
      '% Done': b.coverage != null ? b.coverage + '%' : '',
      'Avg Score': b.avg_score != null ? b.avg_score + '%' : '',
    })));
  }

  if (loading) return <div className="p-8 text-gray-400">Loading dashboard...</div>;

  const { kpis, branches, audit_items } = data;
  const pieData = [
    { name: 'Pass', value: kpis.passed || 0 },
    { name: 'Flag', value: kpis.flagged || 0 },
  ];

  return (
    <div className="p-8 space-y-8">
      {drawer && <JobsDrawer filter={drawer} dateFrom={dateFrom} dateTo={dateTo} branches={branches.map(b => b.branch)} onClose={() => setDrawer(null)} />}

      {/* Header + Date Filter */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Del-Air P&CC — Performance & Compliance Overview</p>
        </div>
        <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} />
      </div>

      {/* Active Audits */}
      <ActiveAudits />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <KPICard label="Total Jobs"  value={kpis.total_jobs?.toLocaleString()}   onClick={() => setDrawer('all')} />
        <KPICard label="Pending"     value={kpis.pending?.toLocaleString()}      color="text-amber-600"  onClick={() => setDrawer('pending')} />
        <KPICard label="In Progress" value={kpis.in_progress?.toLocaleString()}  color="text-[#E8761A]"  sub="Audited, not yet completed" onClick={() => setDrawer('in_progress')} />
        <KPICard label="Completed"   value={kpis.completed?.toLocaleString()}    color="text-blue-600"   onClick={() => setDrawer('completed')} />
        <KPICard label="Passed"      value={kpis.passed?.toLocaleString()}       color="text-green-600"  onClick={() => setDrawer('passed')} />
        <KPICard label="Flagged"     value={kpis.flagged?.toLocaleString()}      color="text-red-600"    onClick={() => setDrawer('flagged')} />
        <KPICard
          label="Pass Rate"
          value={kpis.pass_rate != null ? `${kpis.pass_rate}%` : '—'}
          sub={kpis.avg_score != null ? `Avg score: ${kpis.avg_score}%` : null}
          color={kpis.pass_rate >= 90 ? 'text-green-600' : kpis.pass_rate >= 75 ? 'text-amber-600' : 'text-red-600'}
        />
      </div>

      {/* Branch Scorecard */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">Branch Scorecard</h2>
          <button onClick={exportBranchScorecard} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Download size={13} /> Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#3D2B1A] text-white">
                {['Branch','Total Jobs','Pending','Completed','Passed','Flagged','Pass Rate','% Done','Avg Score'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {branches.map((b, i) => (
                <tr key={b.branch} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/50'}`}>
                  <td className="px-4 py-3 font-semibold text-[#3D2B1A]">{b.branch}</td>
                  <td className="px-4 py-3 text-center text-gray-600">{b.total}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-semibold ${(b.pending??0)>0?'text-amber-600':'text-gray-400'}`}>{b.pending??0}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600">{b.audited??0}</td>
                  <td className="px-4 py-3 text-center text-green-600 font-medium">{b.passed??0}</td>
                  <td className="px-4 py-3 text-center text-red-600 font-medium">{b.flagged??0}</td>
                  <td className="px-4 py-3 text-center">
                    {b.pass_rate != null
                      ? <span className={`font-semibold ${b.pass_rate>=90?'text-green-600':b.pass_rate>=75?'text-amber-600':'text-red-600'}`}>{b.pass_rate}%</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-500">{b.coverage!=null?`${b.coverage}%`:'—'}</td>
                  <td className="px-4 py-3 text-center text-gray-500">{b.avg_score!=null?`${b.avg_score}%`:'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Pass Rate by Branch</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={branches.filter(b => b.pass_rate != null)} margin={{ left: -10 }}>
              <XAxis dataKey="branch" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `${v}%`} domain={[0,100]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => [`${v}%`, 'Pass Rate']} />
              <Bar dataKey="pass_rate" fill="#E8761A" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Pass vs Flag Split</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                   dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                <Cell fill="#1B5E20" /><Cell fill="#B71C1C" />
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Audit Item Failures */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Audit Item Failure Analysis</h2>
            <p className="text-xs text-gray-400 mt-0.5">Which checklist items fail most often</p>
          </div>
          <button onClick={() => exportCsv('audit-item-failures.csv', audit_items.map(i => ({
            Item: i.item, 'No': i.no_count??0, 'Yes': i.yes_count??0, 'N/A': i.na_count??0,
            'Times Audited': i.answered??0, 'Failure Rate': i.fail_rate+'%'
          })))} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Download size={13} /> Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#3D2B1A] text-white">
                {['Audit Item','# No','# Yes','# N/A','Times Audited','Failure Rate'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {audit_items.map((item, i) => (
                <tr key={item.item} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/50'}`}>
                  <td className="px-4 py-3 font-medium text-gray-700">{item.item}</td>
                  <td className="px-4 py-3 text-center text-red-600 font-medium">{item.no_count??0}</td>
                  <td className="px-4 py-3 text-center text-green-600">{item.yes_count??0}</td>
                  <td className="px-4 py-3 text-center text-gray-400">{item.na_count??0}</td>
                  <td className="px-4 py-3 text-center text-gray-500">{item.answered??0}</td>
                  <td className="px-4 py-3">
                    {item.fail_rate > 0
                      ? <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.fail_rate>=20?'bg-red-100 text-red-700':item.fail_rate>=10?'bg-amber-100 text-amber-700':'bg-green-100 text-green-700'}`}>{item.fail_rate}%</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
